import React, { ReactNode, isValidElement, useMemo } from 'react';

import { Box } from '@mui/material';
import { RiErrorWarningLine } from 'react-icons/ri';
import { FormattedMessage } from 'react-intl';

import { useAccount } from '@contexts/AccountProvider';
import { ProgramParameters } from '@enums/programs/programParameters';
import { Programs } from '@enums/programs/programs';
import useGetDelinquency from '@hooks/queries/useGetDelinquency';
import useRetrieveAccountLoanDetails from '@hooks/useRetrieveAccountLoanDetails';
import LendingProgramStatus from '@pages/accountContainer/statusBar/LendingProgramStatus';
import StatusBarItem from '@pages/accountContainer/statusBar/statusBarItem/StatusBarItem';

const ProgramStatus = () => {
    let programStatus: ReactNode = null;
    const { program, account, programHasParameter } = useAccount();
    const { data: lendingData } = useRetrieveAccountLoanDetails({
        enabled: program?.type === Programs.LENDING,
    });
    const isDelinquencyEnabled = useMemo(
        () => programHasParameter(ProgramParameters.DELINQUENCY),
        [programHasParameter],
    );

    const { data: delinquency, isError } = useGetDelinquency({
        accountId: +account?.account_id,
        enabled: isDelinquencyEnabled,
    });
    const totalOverDue = delinquency?.[0]?.calculation_memory?.overdue_amount;

    const isDelinquent = isDelinquencyEnabled && !isError && totalOverDue > 0;

    switch (program?.type) {
        case Programs.LENDING:
            programStatus = LendingProgramStatus({ lendingData: lendingData });
            break;
        case Programs.CREDITO:
            if (isDelinquent) {
                programStatus = (
                    <>
                        <Box display="flex" mr={1} color="error.main">
                            <RiErrorWarningLine data-testid="warning-icon" />
                        </Box>
                        <FormattedMessage
                            id="ProgramStatus.overDue"
                            defaultMessage="Overdue"
                        />
                    </>
                );
            }
            break;
    }

    if (isValidElement(programStatus)) {
        return (
            <StatusBarItem
                tooltipText={
                    <FormattedMessage
                        id="AccountStatus.programStatus"
                        defaultMessage="Program status"
                    />
                }>
                {programStatus}
            </StatusBarItem>
        );
    }
    return null;
};

export default ProgramStatus;
